import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import { ComprehensiveStockAnalysis } from '@/types/perplexity.types';
import { SkeletonCard } from './SkeletonCard';

interface CompanyOverviewCardProps {
  data: ComprehensiveStockAnalysis['companyOverview'] | null | undefined;
  isLoading: boolean;
}

export const CompanyOverviewCard: React.FC<CompanyOverviewCardProps> = ({ data, isLoading }) => {
  if (isLoading || !data) {
    return <SkeletonCard lines={4} />;
  }

  return (
    <View style={styles.card}>
      <View style={styles.cardHeader}>
        <Ionicons name="business" size={20} color={Colors.primary} />
        <Text style={styles.cardTitle}>Company Overview</Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>Business Model</Text>
        <Text style={styles.cardText}>{data.businessModel}</Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>Industry</Text>
        <Text style={styles.cardText}>{data.industry}</Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>Key Products</Text>
        <Text style={styles.cardText}>{data.keyProducts}</Text>
      </View>

      {data.recentNews && data.recentNews.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.label}>Recent News</Text>
          {data.recentNews.map((news, index) => (
            <View key={index} style={styles.bulletItem}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>{news}</Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: Colors.text.primary,
    flex: 1,
  },
  section: {
    marginBottom: 12,
  },
  label: {
    fontSize: 13,
    fontWeight: '600',
    color: Colors.text.secondary,
    marginBottom: 4,
  },
  cardText: {
    fontSize: 14,
    lineHeight: 20,
    color: Colors.text.primary,
  },
  bulletItem: {
    flexDirection: 'row',
    marginTop: 6,
  },
  bullet: {
    fontSize: 14,
    color: Colors.primary,
    marginRight: 8,
    marginTop: 2,
  },
  bulletText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
    color: Colors.text.primary,
  },
});
